import { useMemo } from "react";
import { asNumber, formatValue } from "../../lib/kernelFormat.js";
import { sanitizeRenderColor } from "../../lib/renderSafety.js";
import { boundedLimit } from "../../lib/decisionScales.js";
import { colorForKey, KernelEmpty } from "./shared.jsx";

export const kind = "distribution";

function weightedQuantiles(values, weights) {
    const pairs = values
        .map((value, index) => {
            const rawWeight = asNumber(weights?.[index]);
            return { value: asNumber(value), weight: rawWeight == null ? null : Math.max(0, rawWeight) };
        })
        .filter((pair) => pair.value != null && pair.weight != null && pair.weight > 0)
        .sort((a, b) => a.value - b.value);
    const total = pairs.reduce((sum, pair) => sum + pair.weight, 0);
    const at = (target) => {
        let cumulative = 0;
        for (const pair of pairs) {
            cumulative += pair.weight;
            if (cumulative >= total * target) return pair.value;
        }
        return pairs.at(-1)?.value ?? null;
    };
    return { count: pairs.length, min: pairs[0]?.value ?? null, q1: at(0.25), median: at(0.5), q3: at(0.75), max: pairs.at(-1)?.value ?? null };
}

export function Component({ spec, data, fillHeight = false, onFocus }) {
    const valueField = spec.valueField || "value";
    const labelField = spec.labelField || "label";
    const weightField = spec.weightField || null;
    const maxGroups = boundedLimit(spec.maxGroups, 10, 20);
    const groups = useMemo(() => {
        const byLabel = new Map();
        for (const row of Array.isArray(data) ? data : []) {
            const label = String(row?.[labelField] ?? "All");
            if (!byLabel.has(label)) byLabel.set(label, { label, values: [], weights: [] });
            byLabel.get(label).values.push(row?.[valueField]);
            byLabel.get(label).weights.push(weightField ? row?.[weightField] : 1);
        }
        return [...byLabel.values()]
            .map((group) => ({ ...group, stats: weightedQuantiles(group.values, group.weights) }))
            .filter((group) => group.stats.median != null)
            .sort((a, b) => b.stats.count - a.stats.count)
            .slice(0, maxGroups);
    }, [data, labelField, valueField, weightField, maxGroups]);
    if (!groups.length) return <KernelEmpty message={spec.empty || "No distribution to show"} />;

    const domainMin = Math.min(...groups.map((group) => group.stats.min));
    const domainMax = Math.max(...groups.map((group) => group.stats.max));
    const span = domainMax - domainMin || 1;
    const x = (value) => ((value - domainMin) / span) * 100;
    const format = spec.format || "number";
    const focused = spec.focusValue == null ? null : String(spec.focusValue);

    return (
        <div className={fillHeight ? "flex h-full min-h-0 flex-col" : ""}>
            {spec.title ? <div className="mb-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">{spec.title}</div> : null}
            <div className="space-y-[5px]">
                {groups.map((group) => {
                    const selected = focused != null && focused === group.label;
                    const clickable = Boolean(onFocus);
                    const color = colorForKey(group.label);
                    return (
                        <div
                            key={group.label}
                            role={clickable ? "button" : undefined}
                            tabIndex={clickable ? 0 : undefined}
                            aria-pressed={clickable ? selected : undefined}
                            onClick={clickable ? () => onFocus(spec.groupField || labelField, selected ? null : group.label) : undefined}
                            onKeyDown={clickable ? (event) => {
                                if (event.key !== "Enter" && event.key !== " ") return;
                                event.preventDefault();
                                onFocus(spec.groupField || labelField, selected ? null : group.label);
                            } : undefined}
                            className={`grid grid-cols-[minmax(80px,24%)_minmax(0,1fr)_auto] items-center gap-[8px] text-[10.5px]${clickable ? " cursor-pointer" : ""}`}
                            style={{ opacity: focused != null && !selected ? 0.3 : 1 }}
                        >
                            <span className="truncate font-mono text-[color:var(--ink)]" title={group.label}>{group.label}</span>
                            <div className="relative h-[14px]">
                                <div className="absolute top-[6px] h-px bg-[color:var(--line-strong)]" style={{ left: `${x(group.stats.min)}%`, right: `${100 - x(group.stats.max)}%` }} />
                                <div className="absolute top-[3px] h-[7px] border" style={{ left: `${x(group.stats.q1)}%`, right: `${100 - x(group.stats.q3)}%`, borderColor: sanitizeRenderColor(color), background: sanitizeRenderColor(color), opacity: 0.35 }} />
                                <div className="absolute top-[1px] h-[11px] w-px" style={{ left: `${x(group.stats.median)}%`, background: sanitizeRenderColor(color) }} />
                            </div>
                            <span className="font-mono tabular-nums text-[color:var(--muted)]">{formatValue(group.stats.median, format)}</span>
                        </div>
                    );
                })}
            </div>
            <div className="mt-[6px] flex justify-between font-mono text-[9px] tabular-nums text-[color:var(--muted-soft)]">
                <span>{formatValue(domainMin, format)}</span>
                <span>box: middle 50%; line: median</span>
                <span>{formatValue(domainMax, format)}</span>
            </div>
        </div>
    );
}
